import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './components/Login';
import { FullScreenLoader } from './components/LoadingSpinner';
import { UsersTable } from './components/UsersTable';
import { UserDetail } from './pages/UserDetail';
import { AdminReports } from './pages/AdminReports';
import { DomainOrderReports } from './pages/DomainOrderReports';
import { Reconnects } from './pages/Reconnects';
import { ForwardingDomain } from './pages/tools/ForwardingDomain';
import { Nameservers } from './pages/tools/Nameservers';
import { CreateZone } from './pages/tools/CreateZone';
import { Maintenance } from './pages/tools/Maintenance';
import { GoogleRecovery } from './pages/tools/GoogleRecovery';
import { CheckDomainStatus } from './pages/tools/CheckDomainStatus';
import { GoogleResellerHelper } from './pages/tools/GoogleResellerHelper';
import { CreateManagerAccount } from './pages/CreateManagerAccount';
import ManageTime from './pages/ManageTime';
import { DnsDemo } from './pages/DnsDemo';
import BusinessIntelligence from './pages/BusinessIntelligence';
import PrewarmedDomainsAdmin from './pages/PrewarmedDomainsAdmin';
import { MassInvoice } from './pages/MassInvoice';
import UnpurchasedOrders from './pages/UnpurchasedOrders';
import SmartleadCancels from './pages/SmartleadCancels';

const AppContent: React.FC = () => {
  const { isAuthenticated, login, isLoading } = useAuth();
  const [loginError, setLoginError] = useState<string | undefined>();

  const handleLogin = async (credentials: { username: string; password: string }) => {
    setLoginError(undefined);
    const result = await login(credentials);
    
    if (!result.success) {
      setLoginError(result.error);
    }
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return <FullScreenLoader />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} error={loginError} />;
  }

  // Show main application if authenticated
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<UsersTable defaultPlatform="Instantly" title="Instantly Orders" />} />
        <Route path="/orders/instantly" element={<UsersTable defaultPlatform="Instantly" title="Instantly Orders" />} />
        <Route path="/orders/smartlead" element={<UsersTable defaultPlatform="Smartlead" title="Smartlead Orders" />} />
        <Route path="/orders/manual" element={<UsersTable defaultPlatform="Pager" title="Manual Orders" />} />
        <Route path="/users/:userId" element={<UserDetail />} />
        <Route path="/admin-reports" element={<AdminReports />} />
        <Route path="/domain-order-reports" element={<DomainOrderReports />} />
        <Route path="/reconnects" element={<Reconnects />} />
        <Route path="/manage-time" element={<ManageTime />} />
        <Route path="/mass-invoice" element={<MassInvoice />} />
        <Route path="/tools/forwarding-domain" element={<ForwardingDomain />} />
        <Route path="/tools/nameservers" element={<Nameservers />} />
        <Route path="/tools/create-zone" element={<CreateZone />} />
        <Route path="/tools/maintenance" element={<Maintenance />} />
        <Route path="/tools/google-recovery" element={<GoogleRecovery />} />
        <Route path="/tools/check-domain-status" element={<CheckDomainStatus />} />
        <Route path="/tools/google-reseller" element={<GoogleResellerHelper />} />
        <Route path="/dns-demo" element={<DnsDemo />} />
        <Route path="/admin/prewarmed" element={<PrewarmedDomainsAdmin />} />
        <Route path="/business-intelligence" element={<BusinessIntelligence />} />
        <Route path="/unpurchased-orders" element={<UnpurchasedOrders />} />
        <Route path="/smartlead-cancels" element={<SmartleadCancels />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public route for manager account creation */}
          <Route path="/va-create-account" element={<CreateManagerAccount />} />
          
          {/* All other routes require authentication */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
